import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import { Button, Grid, Stack } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useActions } from 'hooks/useActions';
import { actions as cartActions, selectors as cartSelectors } from 'services/cart';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import SeatDetailsOverlay from './components/SeatDetailsOverlay';
import SpecialSeatTypeOverlay from './components/SpecialSeatTypeOverlay';
import { DEFAULT_TEXT } from './strings';
import { useStyles } from './styled';
import {
  MessageHandlers,
  PhotoPreviewClickedMessage,
  RequestConfigurationMessage,
  RequestSeatStatusesMessage,
  SeatClickedMessage,
  SyosToParentMessage,
  syosToParentMessageSchema,
  wrappedMessageSchema
} from './types';
import { sendConfiguration, sendI18nData, sendMessage, sendSeatStatuses, sendSelectedSeats } from './utils';

// import Img from 'react-image';
// import { FlexBox, MuiBox, MuiCardContent, Typography } from '@kis-technologies/kis-ui';
// import WillCallLogo from 'assets/will_call_icon.png';

const SeatSelectionView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { releaseCart, removeTickets, reserveTickets, getEventDetails } = useActions({
    releaseCart: cartActions.releaseCart,
    removeTickets: cartActions.removeTicketsFromCart,
    reserveTickets: cartActions.reserveTickets,
    getEventDetails: ticketingActions.getEventDetails
  });

  const cart = useSelector(cartSelectors.selectCart);
  const cartError = useSelector(cartSelectors.selectError);
  const cartLoading = useSelector(cartSelectors.selectLoading);
  const cartReserving = useSelector(cartSelectors.selectReserving);
  const eventDetails = useSelector(ticketingSelectors.selectEventDetails);
  const { eventDetails: eventDetailsFetched } = useSelector(ticketingSelectors.selectFetchCompleted);

  const [actionType, setActionType] = useState<'RESERVE' | 'REMOVE' | null>(null);
  const [errorOverlayText, setErrorOverlayText] = useState<string | null>(null);
  const [photoPreviewClickedMessage, setPhotoPreviewClickedMessage] = useState<PhotoPreviewClickedMessage | null>(null);
  const [requestConfigurationMessage, setRequestConfigurationMessage] = useState<RequestConfigurationMessage | null>(
    null
  );
  const [requestSeatStatusesMessage, setRequestSeatStatusesMessage] = useState<RequestSeatStatusesMessage | null>(null);
  const [seatClickedMessage, setSeatClickedMessage] = useState<SeatClickedMessage | null>(null);
  const [selectedSeatIds, setSelectedSeatIds] = useState<Set<string>>(new Set());
  const [showSeatDetailsOverlay, setShowSeatDetailsOverlay] = useState<boolean>(false);
  const [showSpecialSeatTypeOverlay, setShowSpecialSeatTypeOverlay] = useState<boolean>(false);

  const classes = useStyles();

  // ON MOUNT
  useEffect(() => {
    window.addEventListener('message', onMessage);
    if (!eventDetails) getEventDetails();

    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ON REQUEST CONFIGURATION AND EVENT DETAILS FETCHED
  useEffect(() => {
    if (!requestConfigurationMessage || !eventDetailsFetched) return;

    if (eventDetails) {
      sendI18nData(t);
      sendConfiguration(requestConfigurationMessage.requestId, eventDetails);
    } else {
      // TODO: handle case where eventDetails is null
    }
  }, [requestConfigurationMessage, eventDetailsFetched]);

  // ON REQUEST SEAT STATUSES
  useEffect(() => {
    if (!requestSeatStatusesMessage) return;

    setTimeout(() => {
      sendSeatStatuses(requestSeatStatusesMessage.requestId, requestSeatStatusesMessage.zoneIds);
    }, 1000);
  }, [requestSeatStatusesMessage]);

  // ON SEAT CLICKED
  useEffect(() => {
    if (!seatClickedMessage) return;

    // If seat is already selected, deselect/remove it
    const seatId = seatClickedMessage.seat.id.toString();
    if (selectedSeatIds.has(seatId)) {
      removeSeatFromCart();
      return;
    }
    // If seat has special handling, show messaging overlay
    const specialSeatType = seatClickedMessage.seatType.specialHandling;
    if (specialSeatType) {
      setShowSpecialSeatTypeOverlay(true);
      return;
    }
    // Otherwise, show seat details overlay
    setShowSeatDetailsOverlay(true);
  }, [seatClickedMessage]);

  // ON CHANGE OF CART RESERVING/REMOVING/ERROR
  useEffect(() => {
    if (!actionType || cartLoading || cartReserving) return;

    // Handle errors after reserve/remove actions
    if (!cartLoading && !cartReserving && cartError) {
      setErrorOverlayText(actionType === 'RESERVE' ? DEFAULT_TEXT.reserveError : DEFAULT_TEXT.removeError);
      return;
    }

    if (!seatClickedMessage) return;

    // Update selected seat IDs after successful reserve/remove actions
    const seatId = seatClickedMessage.seat.id.toString();
    const tempSelectedSeatIds = new Set(selectedSeatIds);
    if (actionType === 'RESERVE' && !cartReserving) {
      tempSelectedSeatIds.add(seatId);
    } else if (actionType === 'REMOVE' && !cartLoading) {
      tempSelectedSeatIds.delete(seatId);
    }
    setSelectedSeatIds(tempSelectedSeatIds);
    setActionType(null);
    setSeatClickedMessage(null);
  }, [cartReserving, cartLoading, cartError]);

  // ON CHANGE OF SELECTED SEAT IDS
  useEffect(() => {
    sendSelectedSeats(selectedSeatIds);
  }, [selectedSeatIds]);

  const addSeatToCart = () => {
    if (!seatClickedMessage) return;

    // TODO: determine the offer to use based on the zoneId and selected ticket type
    const reserveParams = eventDetails!.offers[0].purchaseParams;
    reserveTickets([reserveParams]);
    setActionType('RESERVE');
  };

  const removeSeatFromCart = () => {
    if (!seatClickedMessage) return;

    if (cart && cart.quantity && cart.quantity === 1) {
      releaseCart();
    } else {
      // TODO: determine the correct removeParams from the cart for the seat
      const removeParams = get(cart, 'products[0].items[0].removeParams', null);
      removeTickets([removeParams]);
    }
    setActionType('REMOVE');
  };

  const onInvalidMessage = (message: SyosToParentMessage) => {
    if (message.type !== 'INVALID_MESSAGE') return;
    // TODO: log these messages using kls?
  };

  const onPhotoPreviewClicked = (message: SyosToParentMessage) => {
    if (message.type !== 'PHOTO_PREVIEW_CLICKED') return;
    setPhotoPreviewClickedMessage(message);
  };

  const onRequestConfiguration = (message: SyosToParentMessage) => {
    if (message.type !== 'REQUEST_CONFIGURATION') return;
    setRequestConfigurationMessage(message);
  };

  const onRequestSeatStatuses = (message: SyosToParentMessage) => {
    if (message.type !== 'REQUEST_SEAT_STATUSES') return;
    setRequestSeatStatusesMessage(message);
  };

  const onSeatClicked = (message: SyosToParentMessage) => {
    if (message.type !== 'SEAT_CLICKED') return;
    setSeatClickedMessage(message);
  };

  const handlers: MessageHandlers = {
    INVALID_MESSAGE: onInvalidMessage,
    PHOTO_PREVIEW_CLICKED: onPhotoPreviewClicked,
    REQUEST_CONFIGURATION: onRequestConfiguration,
    REQUEST_SEAT_STATUSES: onRequestSeatStatuses,
    SEAT_CLICKED: onSeatClicked
  };

  const onMessage = (event: MessageEvent) => {
    const wrappedResult = wrappedMessageSchema.safeParse(event.data);
    if (!wrappedResult.success) return;

    const result = syosToParentMessageSchema.safeParse(wrappedResult.data.payload);
    if (!result.success) {
      // console.log(
      //   "INVALID IN PARENT",
      //   wrappedResult.data.payload,
      //   result.error.message
      // );
      sendMessage({
        type: 'INVALID_MESSAGE',
        message: result.error.message
      });
      return;
    }

    // console.log("KIS REC. --", result.data.type, wrappedResult.data.payload);

    const data = result.data;
    handlers[data.type](data);
  };

  // Navigate back
  const onPrevPageClick = () => {
    navigate(`/ticketing/events`);
  };

  const onSeatDetailsCancel = () => {
    setSeatClickedMessage(null);
    setShowSeatDetailsOverlay(false);
  };

  const onSeatDetailsConfirm = () => {
    addSeatToCart();
    setShowSeatDetailsOverlay(false);
  };

  const onSpecialSeatTypeCancel = () => {
    setSeatClickedMessage(null);
    setShowSpecialSeatTypeOverlay(false);
  };

  const onSpecialSeatTypeConfirm = () => {
    setShowSeatDetailsOverlay(true);
    setShowSpecialSeatTypeOverlay(false);
  };

  return (
    <>
      {/* Loading overlay */}
      {(cartReserving || cartLoading) && <LoadingOverlay style={{ zIndex: '99' }} />}

      <Grid container className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          <iframe
            className={classes.iframeContainer}
            id="syos-iframe"
            sandbox="allow-scripts allow-same-origin"
            src="https://kis-staging.jixaw.com/ten_4vn6f5k2q765v/index.html"
          />
          <Stack className={classes.innerButtonsStack}>
            <Button className={classes.innerButton}>{DEFAULT_TEXT.backToOverview}</Button>
            <Button className={classes.innerButton}>{DEFAULT_TEXT.refreshSeats}</Button>
            <Button className={classes.innerButton}>{DEFAULT_TEXT.togglePhotoPreviews}</Button>
          </Stack>
        </Grid>
      </Grid>

      {/* <Grid container className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          <Grid className={classes.infoContainer}>
            <>
              <MuiCardContent className={classes.ticketImageCard}>
                <Img
                  // src={getEventImageUrl(eventDetails.name, eventDetails.imageUrl)}
                  className={classes.ticketImage}
                  unloader={<img src={WillCallLogo} alt="Event Logo" className={classes.ticketDefaultImage} />}
                />
              </MuiCardContent>
              <MuiBox className={classes.eventDetailsBlock}>
                <Typography className={classes.ticketName}>{'Kiosk Movie Admission'}</Typography>
                <Typography className={classes.ticketDateTime}>
                  {'Tue, Jan 20'}
                  {<> &middot; </>}
                  {'8:00 PM'}
                </Typography>
                <Typography className={classes.ticketDateTime}>
                  {'AR Main Stage'}
                </Typography>
                <Typography className={classes.eventDescription}>{''}</Typography>
              </MuiBox>
            </>
          </Grid>
          <Grid direction="column" className={classes.syosContainer}>
            <FlexBox justify="center">
              <iframe
                className={classes.iframeContainer}
                id="syos-iframe"
                sandbox="allow-scripts allow-same-origin"
                // src="https://eisemann-syos.s3.us-west-2.amazonaws.com/index.html"
                src="https://kis-staging.jixaw.com/ten_4vn6f5k2q765v/index.html"
              />
            </FlexBox>
            <Stack className={classes.innerButtonsStack}>
              <Button className={classes.innerButton}>{DEFAULT_TEXT.backToOverview}</Button>
              <Button className={classes.innerButton}>{DEFAULT_TEXT.refreshSeats}</Button>
              <Button className={classes.innerButton}>{DEFAULT_TEXT.togglePhotoPreviews}</Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid> */}

      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        nextText={t('commonText.next')}
        onNextClick={() => navigate('/ticketing/upsell-selection')}
        isNextDisabled={selectedSeatIds.size === 0}
      />

      {/* Seat details overlay */}
      {showSeatDetailsOverlay && seatClickedMessage && (
        <SeatDetailsOverlay
          seatClickedMessage={seatClickedMessage}
          onCancel={onSeatDetailsCancel}
          onConfirm={onSeatDetailsConfirm}
        />
      )}

      {/* Special seat type messaging overlay */}
      {showSpecialSeatTypeOverlay && seatClickedMessage && (
        <SpecialSeatTypeOverlay
          seatClickedMessage={seatClickedMessage}
          onCancel={onSpecialSeatTypeCancel}
          onConfirm={onSpecialSeatTypeConfirm}
        />
      )}

      {/* Photo preview overlay */}
      {/* TODO: enhance this to show an uploaded image */}
      {photoPreviewClickedMessage && (
        <ActionOverlay
          title={DEFAULT_TEXT.photoPreviewHeader.replace('{{seatId}}', photoPreviewClickedMessage.seatId.toString())}
          confirmText={t('commonText.close')}
          onConfirm={() => setPhotoPreviewClickedMessage(null)}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}

      {/* Error overlay */}
      {errorOverlayText && (
        <ActionOverlay
          title={errorOverlayText}
          confirmText={t('commonText.close')}
          onConfirm={() => setErrorOverlayText(null)}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}
    </>
  );
};

export default SeatSelectionView;
